import React from 'react';
import { AlertCircle, Clock, XCircle, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useAccountStatus } from '@/contexts/AccountStatusContext';
import { useLanguage } from '@/contexts/LanguageContext';

const AccountStatusBanner: React.FC = () => {
  const { accountStatus, isActive } = useAccountStatus();
  const { t } = useLanguage();

  // Don't show banner for active accounts
  if (isActive || !accountStatus) {
    return null;
  }

  const getStatusIcon = () => {
    switch (accountStatus.account_status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'suspended':
        return <AlertCircle className="h-4 w-4" />;
      case 'rejected':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Info className="h-4 w-4" />;
    }
  };

  const getStatusVariant = (): 'default' | 'destructive' => {
    switch (accountStatus.account_status) {
      case 'pending':
        return 'default';
      case 'suspended':
        return 'default';
      case 'rejected':
        return 'destructive';
      default:
        return 'default';
    }
  };

  const getStatusTitle = () => {
    switch (accountStatus.account_status) {
      case 'pending':
        return t('accountStatus.pending.title') || 'Account Under Review';
      case 'suspended':
        return t('accountStatus.suspended.title') || 'Account Temporarily Suspended';
      case 'rejected':
        return t('accountStatus.rejected.title') || 'Account Rejected';
      default:
        return t('accountStatus.default.title') || 'Account Status';
    }
  };

  const getStatusDescription = () => {
    switch (accountStatus.account_status) {
      case 'pending':
        return t('accountStatus.pending.description') || 'Your account is currently under review. You can view your data but cannot make changes until approval.';
      case 'suspended':
        return t('accountStatus.suspended.description') || 'Your account has been temporarily suspended. You can view your data but cannot make changes.';
      case 'rejected':
        return t('accountStatus.rejected.description') || 'Your account has been permanently rejected. You can only view and export your data.';
      default:
        return t('accountStatus.default.description') || 'Your account status is not active.';
    }
  };

  const getWhatYouCanDo = () => {
    switch (accountStatus.account_status) {
      case 'pending':
        return t('accountStatus.pending.canDo') || 'You can view existing projects, tenders, and history.';
      case 'suspended':
        return t('accountStatus.suspended.canDo') || 'You can view your data and contact support for assistance.';
      case 'rejected':
        return t('accountStatus.rejected.canDo') || 'You can view your data and request data export.';
      default:
        return t('accountStatus.default.canDo') || 'You can view your existing data.';
    }
  };

  const getRestoreAccess = () => {
    switch (accountStatus.account_status) {
      case 'pending':
        return t('accountStatus.pending.restoreAccess') || 'Please wait for admin approval. Check your email for updates.';
      case 'suspended':
        return t('accountStatus.suspended.restoreAccess') || 'Contact support to resolve suspension.';
      case 'rejected':
        return t('accountStatus.rejected.restoreAccess') || 'Contact admin if you believe this is an error.';
      default:
        return t('accountStatus.default.restoreAccess') || 'Contact support for assistance.';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Alert variant={getStatusVariant()} className="mb-6">
      <div className="flex items-start gap-3">
        {getStatusIcon()}
        <div className="flex-1">
          <div className="font-semibold mb-2">{getStatusTitle()}</div>
          
          {accountStatus.account_reason && (
            <div className="mb-2">
              <strong>{t('accountStatus.reason') || 'Reason'}:</strong> {accountStatus.account_reason}
            </div>
          )}
          
          {accountStatus.account_status_updated_at && (
            <div className="mb-2 text-sm opacity-75">
              {t('accountStatus.date') || 'Date'}: {formatDate(accountStatus.account_status_updated_at)}
            </div>
          )}
          
          <AlertDescription className="mb-3">
            {getStatusDescription()}
          </AlertDescription>
          
          <div className="text-sm space-y-1">
            <div>
              <strong>{t('accountStatus.whatYouCanDo') || 'What you can do'}:</strong> {getWhatYouCanDo()}
            </div>
            <div>
              <strong>{t('accountStatus.restoreAccess') || 'How to restore access'}:</strong> {getRestoreAccess()}
            </div>
          </div>
          
          {(accountStatus.account_status === 'suspended' || accountStatus.account_status === 'rejected') && (
            <div className="mt-4">
              <Button variant="outline" size="sm">
                {t('accountStatus.contactSupport') || 'Contact Support'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </Alert>
  );
};

export default AccountStatusBanner;
